import React from "react";
import { AxiosError } from "axios";
import { ConfirmationModal } from "@packsize/components";
import { FormattedMessage, useIntl } from "react-intl";
import { IconButton, Typography } from "@mui/material";
import DeleteOutlineRounded from "@mui/icons-material/DeleteOutlineRounded";

import { type CartonPropertyGroup } from "@/schemas/carton-property-group";
import { useDeleteCartonPropertyGroup } from "@/hooks/carton-property-groups/use-delete-carton-property-group";

interface CartonPropertyGroupsDeleteButtonProps {
	readonly cartonPropertyGroup: CartonPropertyGroup;
}

export const CartonPropertyGroupDeleteButton: React.FC<CartonPropertyGroupsDeleteButtonProps> = ({
	cartonPropertyGroup,
}) => {
	const intl = useIntl();

	const [showDeleteConfirmation, setShowDeleteConfirmation] = React.useState(false);

	const { mutate, isPending, error } = useDeleteCartonPropertyGroup(cartonPropertyGroup.id);

	const handleDeleteConfirmed = () => {
		mutate(undefined, {
			onSuccess: () => {
				setShowDeleteConfirmation(false);
			},
			onError: (error) => {
				if (error instanceof AxiosError && error.status === 404) {
					setShowDeleteConfirmation(false);
				}
			},
		});
	};

	return (
		<>
			<ConfirmationModal
				cancelText={intl.formatMessage({ id: "Cancel", defaultMessage: "Cancel" })}
				confirmText={intl.formatMessage({ id: "Delete", defaultMessage: "Delete" })}
				onCancel={() => setShowDeleteConfirmation(false)}
				onClose={() => setShowDeleteConfirmation(false)}
				onConfirm={handleDeleteConfirmed}
				open={showDeleteConfirmation}
				size="sm"
				title={intl.formatMessage(
					{
						id: "Delete {alias}?",
						defaultMessage: "Delete {alias}?",
					},
					{
						alias: cartonPropertyGroup.alias,
					},
				)}
				slotProps={{
					confirmButton: {
						"data-testid": "carton-property-group-delete-confirmation-modal__button__confirm",
						loading: isPending,
					},
					cancelButton: {
						"data-testid": "carton-property-group-delete-confirmation-modal__button__cancel",
					},
				}}
			>
				<Typography>
					<FormattedMessage
						id="Are you sure you want to delete this carton property group?"
						defaultMessage="Are you sure you want to delete this carton property group?"
					/>
				</Typography>
				<Typography>
					<FormattedMessage id="This action cannot be undone." defaultMessage="This action cannot be undone." />
				</Typography>

				{Boolean(error) && (
					<Typography
						sx={{ marginTop: 2 }}
						color="error"
						data-testid="carton-property-group-delete-confirmation-modal__error"
					>
						<FormattedMessage
							id="Failed to delete carton property group. Please try again."
							defaultMessage="Failed to delete carton property group. Please try again."
						/>
					</Typography>
				)}
			</ConfirmationModal>
			<IconButton
				onClick={() => setShowDeleteConfirmation(true)}
				data-testid="carton-property-group-delete-button__button__open-modal"
				data-pendoid="carton-property-groups__button__delete"
			>
				<DeleteOutlineRounded />
			</IconButton>
		</>
	);
};
